import './add-method-sheet.scss';

import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Divider } from '@sola/ark-core/atoms/divider/divider.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import {
  Sheet,
  SheetActions,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@sola/ark-core/molecules/sheet/sheet.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';
import {
  PaymentMethodTypesEnum,
  isPaymentMethodBank,
  isPaymentMethodCard,
} from '@sola/shared-utils/my/payment-method-type-guards.js';

import { AddPaymentMethodForm } from '../../molecules/add-payment-method-form/add-payment-method-form';
import { PaymentACHFields } from '../../molecules/add-payment-method-form/payment-ach-fields';
import { PaymentCCFields } from '../../molecules/add-payment-method-form/payment-cc-fields';
import { CommitmentsBadges } from '../../molecules/commitment-badges/commitment-badges';
import { PaymentMethodSwitch } from '../../molecules/payment-method-switch/payment-method-switch';

export interface AddMethodSheetCommitmentListProps {
  commitments: GraphQLCommitment[];
}

export const AddMethodSheetCommitmentList: React.FC<
  AddMethodSheetCommitmentListProps
> = ({ commitments }) => {
  const { t } = useTranslation('my');

  return (
    <>
      <Divider purpose="neutral" className="my-comfy-xl" />

      <div className="AddMethodSheet__commitment-badges flex flex-col lg:flex-row md:flex-row w-full lg:items-center md:items-center gap-comfy-sm justify-between">
        <h3 className="body-sm">{t('addPaymentMethod.commitmentSummary')}</h3>

        <CommitmentsBadges
          commitments={commitments}
          layout="spaced"
          itemsToShow={5}
        />
      </div>
    </>
  );
};

export const getComponentData = (commitments?: GraphQLCommitment[]) => {
  return {
    recurringCommitments: commitments?.length
      ? commitments
          .filter((commitment) => commitment.isRecurring)
          .sort((a, b) => {
            if (
              a.commitmentType === 'ChildSponsorship' &&
              b.commitmentType !== 'ChildSponsorship'
            ) {
              return -1;
            }
            if (
              a.commitmentType !== 'ChildSponsorship' &&
              b.commitmentType === 'ChildSponsorship'
            ) {
              return 1;
            }
            return 0;
          })
      : null,
  };
};

export interface AddMethodSheetProps {
  commitments?: GraphQLCommitment[];
  /**
   * This should only take in elements that resolve to html buttons.
   */
  openTrigger: ReactElement;
}
export const AddMethodSheet: React.FC<AddMethodSheetProps> = ({
  openTrigger,
  commitments = [],
}) => {
  const { t } = useTranslation('my');

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodTypesEnum>(
    PaymentMethodTypesEnum.Bank,
  );
  const { recurringCommitments } = getComponentData(commitments);

  return (
    <Sheet>
      <SheetTrigger>{openTrigger}</SheetTrigger>
      <SheetContent
        title={t('addPaymentMethod.form.title')}
        footer={
          <SheetActions
            secondaryBtn={{
              children: (
                <SheetClose>
                  {t('addPaymentMethod.sheetAction.cancel')}
                </SheetClose>
              ),
            }}
            primaryBtn={{
              iconBefore: <ArkIcon iconName="lock-01" />,
              children: (
                <button type="button" onClick={() => console.log('click')}>
                  {t('addPaymentMethod.sheetAction.add')}
                </button>
              ),
            }}
          />
        }
      >
        <div>
          <PaymentMethodSwitch setSelectedPaymentMethod={setSelectedMethod} />

          {isPaymentMethodBank(selectedMethod) && (
            <>
              <Alert
                variant="positive"
                title={t('addPaymentMethod.alert.title')}
                message={t('addPaymentMethod.alert.message')}
              />

              <Divider purpose="neutral" className="my-comfy-xl" />

              <AddPaymentMethodForm
                formLabel={t('addPaymentMethod.form.formTitle.bank')}
              >
                <PaymentACHFields />
              </AddPaymentMethodForm>
            </>
          )}

          {isPaymentMethodCard(selectedMethod) && (
            <>
              <Divider purpose="neutral" className="my-comfy-xl" />
              <AddPaymentMethodForm
                formLabel={t('addPaymentMethod.form.formTitle.card')}
              >
                <PaymentCCFields />
              </AddPaymentMethodForm>
            </>
          )}

          {!!recurringCommitments?.length && (
            <AddMethodSheetCommitmentList commitments={recurringCommitments} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
